import { shouldCopyInsteadOfMergeForKey } from '@/app/log-viewer/table/model/shouldCopyInsteadOfMerge';

import {
  ArrayNode,
  getKeyAndValueForValueNode,
  getKeyIfNotToValueNode,
  HighlightableJson,
  isHighlightableJson,
  Node,
  NodeType,
  ObjectChildLink,
  ObjectNode,
  ValueNode,
} from '../HighlightableJson';
import { HighlightableString, isHighlightableString } from '../HighlightableString';
import {
  HighlightableStringExpandableContainer,
  HighlightableStringExpandableContainerState,
  isHighlightableStringExpandableContainer,
} from '../HighlightableStringExpandableContainer';

export interface CallbackResult {
  occurancesFound: boolean;
  shouldCollapseIfNoOccurancesFound: boolean | undefined;
}

type Callback = (s: HighlightableString) => CallbackResult;

type EnumerationArgs = {
  input: any;
  returnFullHighlightedString: boolean;
  callback: Callback;
};

// Return changes (Partial<> for HighlightableString.highlighted).
export function enumerateEachHighlightableString(args: EnumerationArgs): any {
  if (typeof args.input !== 'object') {
    return;
  }
  if (args.input === null) {
    return;
  }

  if (isHighlightableStringExpandableContainer(args.input)) {
    const container = args.input as HighlightableStringExpandableContainer;

    const isAlreadyExpanded =
      container.expandingState !== HighlightableStringExpandableContainerState.Collapsed;
    const shouldLookForInThisContainer = container.shouldExpandForHighlighting || isAlreadyExpanded;

    if (shouldLookForInThisContainer) {
      let callbackResult: CallbackResult = {
        occurancesFound: false,
        shouldCollapseIfNoOccurancesFound: false,
      };

      let changes: any = callForEachKeyAndReturnChanges({
        input: args.input,
        returnFullHighlightedString: args.returnFullHighlightedString,
        callback: (s) => {
          const inputCallbackResult = args.callback(s);
          if (inputCallbackResult.occurancesFound) {
            callbackResult = inputCallbackResult;
          }
          if (typeof inputCallbackResult.shouldCollapseIfNoOccurancesFound !== 'undefined') {
            callbackResult.shouldCollapseIfNoOccurancesFound =
              inputCallbackResult.shouldCollapseIfNoOccurancesFound;
          }
          return inputCallbackResult;
        },
      });

      const shouldExpand =
        callbackResult.occurancesFound &&
        container.expandingState === HighlightableStringExpandableContainerState.Collapsed;
      const shouldCollapse =
        !callbackResult.occurancesFound &&
        callbackResult.shouldCollapseIfNoOccurancesFound &&
        container.expandingState ===
          HighlightableStringExpandableContainerState.ExpandedForHighlighting;

      let isExpandingStateChanged = false;
      if (shouldExpand) {
        container.expandingState =
          HighlightableStringExpandableContainerState.ExpandedForHighlighting;
        isExpandingStateChanged = true;
      } else if (shouldCollapse) {
        container.expandingState = HighlightableStringExpandableContainerState.Collapsed;
        isExpandingStateChanged = true;
      }

      if (isExpandingStateChanged) {
        if (typeof changes === 'undefined') {
          changes = {};
        }
        changes.expandingState = container.expandingState;
      }

      return changes;
    }
  } else if (isHighlightableString(args.input)) {
    const hs = args.input as HighlightableString;
    args.callback(hs);

    return args.returnFullHighlightedString
      ? hs
      : {
          highlighted: hs.highlighted,
        };
  } else if (Array.isArray(args.input)) {
    const changes: any[] = [];
    let hasSomeChanges = false;

    args.input.forEach((o2) => {
      const elementChanges = enumerateEachHighlightableString({
        input: o2,
        callback: args.callback,
        returnFullHighlightedString: args.returnFullHighlightedString,
      });
      changes.push(elementChanges);
      if (typeof elementChanges !== 'undefined') {
        hasSomeChanges = true;
      }
    });

    return hasSomeChanges ? changes : undefined;
  } else {
    return callForEachKeyAndReturnChanges(args);
  }
}

function callForEachKeyAndReturnChanges(args: EnumerationArgs): any {
  if (isHighlightableJson(args.input)) {
    return callForEachHighlightableStringInHighlightableJsonAndReturnChanges(args);
  }

  if (args.input instanceof Map) {
    const changes = new Map();

    // Keys in Map are always taken as if they were sorted.
    Array.from(args.input.keys())
      .sort()
      .forEach((key) => {
        const value = args.input.get(key);
        const change = enumerateEachHighlightableString({
          input: value,
          callback: args.callback,
          returnFullHighlightedString: args.returnFullHighlightedString,
        });

        if (shouldCopyInsteadOfMergeForKey({ key, source: value })) {
          changes.set(key, value);
        } else if (typeof change !== 'undefined') {
          changes.set(key, change);
        }
      });

    return changes.size > 0 ? changes : undefined;
  }

  const changes: any = {};

  Object.keys(args.input).forEach((key) => {
    const value = args.input[key];
    const change = enumerateEachHighlightableString({
      input: value,
      callback: args.callback,
      returnFullHighlightedString: args.returnFullHighlightedString,
    });

    if (shouldCopyInsteadOfMergeForKey({ key, source: value })) {
      changes[key] = value;
    } else if (typeof change !== 'undefined') {
      changes[key] = change;
    }
  });

  return Object.keys(changes).length > 0 ? changes : undefined;
}

function callForEachHighlightableStringInHighlightableJsonAndReturnChanges(
  args: EnumerationArgs,
): any {
  const hjson = args.input as HighlightableJson;
  const changes: any = callForEachHighlightableStringInHighlightableJsonNodeAndReturnChanges(
    hjson.rootNode,
    undefined,
    hjson.originalObject,
    args,
  );
  return typeof changes !== 'undefined'
    ? {
        rootNode: changes,
      }
    : undefined;
}

function callForEachHighlightableStringInHighlightableJsonNodeAndReturnChanges(
  node: Node,
  key: string | undefined,
  value: any,
  args: EnumerationArgs,
): any {
  switch (node.type) {
    case NodeType.Value:
      const valueNode = node as ValueNode;
      const hstr = getKeyAndValueForValueNode(valueNode, key, value);
      args.callback(hstr);
      if (typeof hstr.highlighted === 'undefined') {
        return undefined;
      }
      valueNode.keyAndValue = hstr;
      return {
        keyAndValue: {
          highlighted: hstr.highlighted,
        },
      };
    case NodeType.Array:
      const arrayNode = node as ArrayNode;
      let hasChanges = false;
      const childrenChanges = arrayNode.children.map((childNode, index) => {
        const childChanges = callForEachHighlightableStringInHighlightableJsonNodeAndReturnChanges(
          childNode,
          undefined,
          value[index],
          args,
        );
        if (typeof childChanges !== 'undefined') {
          hasChanges = true;
        }
        return childChanges;
      });
      return hasChanges ? { children: childrenChanges } : undefined;
    case NodeType.Object:
      const objectNode = node as ObjectNode;
      let hasChanges2 = false;
      const childrenChanges2 = new Map<string, DeepPartial<ObjectChildLink>>();
      Array.from(objectNode.children.entries())
        .sort((e1, e2) => e1[0].localeCompare(e2[0]))
        .forEach((entry) => {
          const key = entry[0];

          let childChanges = callForEachHighlightableStringInHighlightableJsonNodeAndReturnChanges(
            entry[1].child,
            key,
            value[key],
            args,
          );

          if (typeof entry[1].keyIfNotToValueNode !== 'undefined') {
            const keyHStr = getKeyIfNotToValueNode(entry[1], key);
            if (keyHStr) {
              args.callback(keyHStr);
              if (typeof keyHStr.highlighted !== 'undefined') {
                entry[1].keyIfNotToValueNode = keyHStr;
                hasChanges2 = true;

                if (typeof childChanges === 'undefined') {
                  childChanges = {};
                }
                childChanges.keyIfNotToValueNode = keyHStr;
              }
            }
          }

          if (typeof childChanges !== 'undefined') {
            childrenChanges2.set(key, {
              child: childChanges,
            });
            hasChanges2 = true;
          }

          return childChanges;
        });
      return hasChanges2 ? { children: childrenChanges2 } : undefined;
  }
}
